import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { RevealLines } from "./Reveal";
import { IMAGES } from "../lib/images";

const easing = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yArt = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const scaleArt = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      ref={ref}
      className="grain relative flex min-h-screen items-center overflow-hidden bg-navy"
      data-testid="hero-section"
    >
      {/* ambient background */}
      <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-gold/10 blur-[140px]" />
        <div className="absolute right-0 top-0 h-[600px] w-[600px] rounded-full bg-gold/5 blur-[160px]" />
      </motion.div>

      <div className="container-luxe relative z-10 grid grid-cols-1 items-center gap-14 pt-28 pb-20 lg:grid-cols-2 lg:gap-10 lg:pt-24 lg:pb-0">
        {/* left — copy */}
        <motion.div style={{ opacity }} className="max-w-xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="overline mb-8 flex items-center gap-4"
          >
            <span className="h-px w-10 bg-gold" /> Bienestar & Desarrollo Consciente
          </motion.p>

          <h1 className="font-serif text-[2.75rem] font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-[4.5rem]">
            <RevealLines lines={["Comprende tu historia."]} delay={0.3} />
            <RevealLines lines={["Transforma tu vida."]} delay={0.46} />
            <RevealLines
              lines={["Todo comenzó con", "un primer latido."]}
              lineClassName="italic text-[#C9A45C]"
              delay={0.62}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-9 max-w-lg text-base font-light leading-relaxed text-cream/70 sm:text-lg"
          >
            La Academia Desde el Primer Latido™ reúne programas, libros y herramientas diseñadas para
            ayudarte a comprender tu historia, sanar patrones emocionales y desarrollar una vida más
            consciente, libre y auténtica.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-11 flex flex-wrap items-center gap-4"
          >
            <button onClick={() => navigate("/academy")} className="btn-ghost" data-testid="hero-cta-explore">
              Explorar la Academia
            </button>
            <a
              href="https://www.skool.com/academiadesdeelprimerlatido-9718/about"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
              data-testid="hero-cta-start"
            >
              Comenzar ahora <ArrowRight size={15} />
            </a>
          </motion.div>
        </motion.div>

        {/* right — premium visual composition */}
        <motion.div style={{ opacity }} className="relative flex justify-center lg:justify-end">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, delay: 0.4, ease: easing }}
            style={{ y: yArt, scale: scaleArt }}
            className="relative w-full max-w-md"
          >
            {/* soft golden aura */}
            <div className="absolute inset-0 -z-10 scale-90 rounded-full bg-gold/15 blur-[90px]" />

            <motion.img
              src={IMAGES.heroSilhouette}
              alt="Silueta humana con luz dorada y conexiones neuronales"
              className="relative w-full select-none"
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* elegant floating particles */}
            {[
              { l: "12%", t: "22%", s: 4, d: 0 },
              { l: "82%", t: "30%", s: 3, d: 1.2 },
              { l: "20%", t: "68%", s: 3, d: 0.6 },
              { l: "76%", t: "72%", s: 5, d: 1.8 },
              { l: "50%", t: "12%", s: 3, d: 0.9 },
            ].map((p, i) => (
              <motion.span
                key={`${p.l}-${p.t}`}
                className="absolute rounded-full bg-gold"
                style={{ left: p.l, top: p.t, width: p.s, height: p.s }}
                animate={{ opacity: [0.15, 0.9, 0.15], scale: [1, 1.6, 1] }}
                transition={{ duration: 3.5, delay: p.d, repeat: Infinity, ease: "easeInOut" }}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => go("filosofia")}
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 text-gold/70 sm:flex"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[0.6rem] uppercase tracking-luxe">Desliza</span>
        <ArrowDown size={16} />
      </motion.button>
    </section>
  );
};
